import { useCallback, useMemo, useRef, useState } from 'react';
import { ChakraEmptyList } from 'app/components/Core';
import {
  getOpenInNewTabMenuOption,
  useContextMenu,
} from 'app/components/ChakraTableList';
import { ReactComponent as PlaylistsIcon } from 'resources/images/playlists-huge.svg';
import { PlaylistsListItem } from 'types/playlists.types';
import { useInfiniteLoader } from 'app/utils/hooks';
import {
  deletePlaylistEffect,
  fetchPlaylistsEffect,
} from 'app/store/Playlists/effects';
import { getPlaylistsNextLink } from 'app/store/Playlists/selectors';
import { routes } from 'app/routing/routesConfig';
import { DeletePlaylistDialog } from 'app/components/Playlist/DeletePlaylistDialog';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  useAudioPlayer,
  usePlayerPlaylist,
} from 'app/components/AudioPlayer/hooks';
import { PlaylistShareLinkDialog } from 'app/components/Playlist/PlaylistShareLinkDialog';
import { getPlaylistDetails } from 'app/store/AudioPlayer/selectors';
import { isPlaylistShareModalOpen } from 'app/store/Shares/selectors';
import { setShareModal } from 'app/store/Shares/slice';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { Row } from '@tanstack/react-table';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { getColumns } from './utils';
import { PlaylistRow } from './PlaylistRow';

export interface PlaylistsListProps {
  playlists: PlaylistsListItem[];
  loading?: boolean;
}
export const PlaylistsList = ({
  playlists = [],
  loading = false,
}: PlaylistsListProps) => {
  const dispatch = useAppDispatch();
  const isShareModalOpen = useAppSelector(isPlaylistShareModalOpen);
  const setIsShareModalOpen = useCallback(
    (isOpen: boolean) => dispatch(setShareModal({ isOpen, type: 'playlist' })),
    [dispatch]
  );

  const [, infiniteScrollProps, loadingMore] = useInfiniteLoader({
    dataLength: playlists.length,
    nextLinkSelector: getPlaylistsNextLink,
    fetchNext: (link) => fetchPlaylistsEffect({ next: link }),
  });
  const [isConfirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const selectedPlaylistRef = useRef<PlaylistsListItem>();
  const {
    loadPlaylist,
    loadingPlaylistGuid,
    isPlayerLoading,
    isPlaying,
  } = usePlayerPlaylist();
  const playlistDetailsInPlayer = useAppSelector(getPlaylistDetails);
  const { stop } = useAudioPlayer();

  const ref = useRef<HTMLDivElement>(null);
  const tableKey = 'playlists';
  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(() => getColumns(totalWidth), [totalWidth]);

  const contextMenuProps = useContextMenu();
  const rowRenderer = useCallback(
    (item: Row<PlaylistsListItem>) => {
      const playlist = item.original;

      const href = routes.playlist.path.replace(':guid', playlist.guid);
      const options = [
        ...getOpenInNewTabMenuOption(href),
        {
          title: 'Share',
          value: 'SHARE',
          onClick: () => {
            selectedPlaylistRef.current = playlist;
            setIsShareModalOpen(true);
          },
        },
        {
          title: 'Delete',
          value: 'DELETE',
          onClick: () => {
            selectedPlaylistRef.current = playlist;
            setConfirmDeleteOpen(true);
          },
        },
      ];

      return (
        <PlaylistRow
          key={playlist.guid}
          playlist={playlist}
          href={href}
          isPlayerLoading={isPlayerLoading}
          isPlaying={isPlaying}
          loadPlaylist={loadPlaylist}
          loadingPlaylistGuid={loadingPlaylistGuid}
          contextHandler={(e) =>
            contextMenuHandler(e, contextMenuProps, options)
          }
        />
      );
    },
    [
      contextMenuProps,
      isPlayerLoading,
      isPlaying,
      loadPlaylist,
      loadingPlaylistGuid,
      setIsShareModalOpen,
    ]
  );

  if (!loading && playlists.length === 0) {
    return (
      <ChakraEmptyList
        icon={<PlaylistsIcon role="img" />}
        title="Your home for Playlists"
        primaryText="Build your own playlist for listening and sharing to A&R or future collaborators"
        primaryTextSx={{ maxWidth: '630px' }}
      />
    );
  }

  return (
    <>
      <StickyTable<PlaylistsListItem>
        tableKey={tableKey}
        ref={ref}
        data={playlists}
        columns={columns}
        infiniteScrollProps={infiniteScrollProps}
        loadingMore={loadingMore}
        rowRenderer={rowRenderer}
        contextMenuProps={contextMenuProps}
        loading={loading}
      />
      <PlaylistShareLinkDialog
        isOpen={isShareModalOpen}
        playlistTitle={selectedPlaylistRef.current?.title || ''}
        onClose={() => setIsShareModalOpen(false)}
        playlistGuid={selectedPlaylistRef.current?.guid || ''}
      />
      <DeletePlaylistDialog
        open={isConfirmDeleteOpen}
        playlistName={selectedPlaylistRef.current?.title || ''}
        onClose={() => {
          setConfirmDeleteOpen(false);
        }}
        handleAction={() => {
          if (selectedPlaylistRef.current?.guid) {
            setConfirmDeleteOpen(false);
            dispatch(
              deletePlaylistEffect({
                playlistGuid: selectedPlaylistRef.current.guid,
              })
            );
            if (
              playlistDetailsInPlayer &&
              playlistDetailsInPlayer.guid === selectedPlaylistRef.current?.guid
            ) {
              stop();
            }
            selectedPlaylistRef.current = undefined;
          }
        }}
      />
    </>
  );
};
